import React, { useCallback, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type WebView from 'react-native-webview';
import { HostedFrame, theme, useResponsive } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { CreditCardFormControlledContent } from '../../components/CreditCard/CreditCardFormControlledContent';
import { CreditCardFormErrorState } from '../../components/CreditCard/CreditCardFormErrorState';
import { CreditCardFormLoadingState } from '../../components/CreditCard/CreditCardFormLoadingState';
import { useCreditCardForm } from '../../hooks/useCreditCardForm';
import {
  useStripeFormSubmission,
  useStripeKeys,
  useStripeMessages,
} from './hooks';
import { FRAME_HEIGHT, getStripeSource, STRIPE_FRAME_ID } from './utils';

export const StripeCreditCardForm = (props: StripeCreditCardFormProps) => {
  const { excludedFields, isSubmitting, handleOnSave, handleOnApplePay } =
    props;

  // ─── Context ─────────────────────────────────────────────────────────

  const webViewRef = useRef<WebView>(null);
  const { match } = useResponsive();
  const { track } = useTelemetry();
  const { t } = useLocalizationContext();

  // ─── Controlled Form State ───────────────────────────────────────────

  const { formData, updateForm } = useCreditCardForm({ default: true });

  // ─── Submission Results ──────────────────────────────────────────────

  const handleSuccess = useCallback(
    (id: string) => {
      track('payment_credit.save_card');

      handleOnSave({
        id,
        nickname: formData.nickname,
        isDefault: formData.default,
      });
    },
    [formData.default, formData.nickname, track, handleOnSave],
  );

  // ─── Stripe Initialization ───────────────────────────────────────────

  const { handleSubmitStripeForm, handleStripeError } = useStripeFormSubmission(
    { webViewRef },
  );

  const { stripeKey, clientSecret, isFetchingStripeKeys, isMissingStripeKeys } =
    useStripeKeys();

  const postalCodeErrorMessage = t('credit-card-form.error.banner.zipCode');
  const stripeWebSource = getStripeSource({
    stripeKey,
    clientSecret,
    postalCodeErrorMessage,
  });

  const { isSubmittingStripe, handleStripeMessages } = useStripeMessages({
    onSuccess: handleSuccess,
    onError: handleStripeError,
  });

  if (isMissingStripeKeys) {
    return <CreditCardFormErrorState />;
  }

  return (
    <CreditCardFormControlledContent
      canSubmitForm
      mode="add"
      isSaving={isSubmittingStripe || isSubmitting}
      form={formData}
      excludedFields={excludedFields}
      onFormChange={updateForm}
      onSubmitForm={handleSubmitStripeForm}
      onApplePay={handleOnApplePay}
    >
      <View style={styles.wrapper}>
        <CreditCardFormLoadingState
          style={styles.wrapper}
          show={isFetchingStripeKeys}
        />
        <HostedFrame
          ref={webViewRef}
          frameId={STRIPE_FRAME_ID}
          frameHeight={FRAME_HEIGHT}
          title={t('credit-card-form.title')}
          isLoading={isFetchingStripeKeys}
          source={stripeWebSource}
          onMessage={handleStripeMessages}
          style={styles.content}
          containerStyle={[
            match([styles.containerXs, styles.containerMd]),
            isFetchingStripeKeys && styles.loadingFields,
          ]}
        />
      </View>
    </CreditCardFormControlledContent>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type StripeCreditCardFormProps = Readonly<{
  excludedFields?: ReadonlyArray<'nickname' | 'isDefault'>;
  isSubmitting: boolean;
  handleOnSave: (payload: CreditCardForm) => void;
  handleOnApplePay?: () => void;
}>;

type CreditCardForm = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
}>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: theme.colors.OATMEAL,
    height: FRAME_HEIGHT,
  },
  containerXs: {
    backgroundColor: theme.colors.OATMEAL,
    paddingHorizontal: theme.spacing['4'],
    height: FRAME_HEIGHT,
  },
  containerMd: {
    backgroundColor: theme.colors.OATMEAL,
    paddingHorizontal: theme.spacing['6'],
    height: FRAME_HEIGHT,
  },
  content: {
    backgroundColor: theme.colors.OATMEAL,
  },
  // for web views to function properly on Android, their container must be at least 1x1 in size
  loadingFields: {
    flex: 0,
    width: 1,
    height: 1,
  },
});
